/**
 * ACTIVITY-CORE (L2) — restore / purge catalog trash items.
 *
 * Restore = `deleted_at: null` (the server records it as `restored`).
 * Purge = hard DELETE (PowerSync DELETE op; the server leaves a complete
 * snapshot in the patch as `deleted`). The purge manually cascades catalog
 * links (recipe, modifiers, variants, combo items).
 */
import {
  combo,
  combo_item,
  customer,
  employee,
  eq,
  ingredient,
  menu_category,
  menu_product,
  type PowerSyncDrizzleDB,
  product_modifier_group,
  product_variant,
  promotion,
  recipe_item,
  supplier,
} from "@comandero/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import type { TrashEntityType } from "./queries";

const TABLES = {
  menu_category,
  menu_product,
  product_variant,
  combo,
  ingredient,
  supplier,
  employee,
  customer,
  promotion,
} as const;

/**
 * Restores an item from the trash (soft-undelete). `UPDATE … WHERE id = ?`
 * through `patchRowVerified`: a raw UPDATE against the PowerSync view can match
 * ZERO rows and return "success" while the newly created row is still traveling
 * to the server (see `../powersync/verified-write.ts`). It may throw: the caller
 * reports it instead of swallowing it.
 */
export async function restoreTrashItemMutation(
  db: PowerSyncDrizzleDB,
  entityType: TrashEntityType,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const table = TABLES[entityType];
  await patchRowVerified(db, table, table.id, id, { deleted_at: null }, options);
}

/** Permanent purge (hard DELETE) with a manual cascade of catalog links. */
export async function purgeTrashItemMutation(
  db: PowerSyncDrizzleDB,
  entityType: TrashEntityType,
  id: string,
): Promise<void> {
  if (entityType === "menu_product") {
    await db.delete(product_modifier_group).where(eq(product_modifier_group.product_id, id));
    await db.delete(recipe_item).where(eq(recipe_item.product_id, id));
    await db.delete(product_variant).where(eq(product_variant.product_id, id));
    await db.delete(combo_item).where(eq(combo_item.product_id, id));
  }
  if (entityType === "combo") {
    await db.delete(combo_item).where(eq(combo_item.combo_id, id));
  }
  if (entityType === "ingredient") {
    await db.delete(recipe_item).where(eq(recipe_item.ingredient_id, id));
  }
  const table = TABLES[entityType];
  await db.delete(table).where(eq(table.id, id));
}
